import { isModelPastFrontendSunset, modelsData } from '../constants/models';

// Tab-local, versioned, deliberately separate from legacy settings and threads.
export const CHAT_DRAFT_LANDING = '/chat';
export const CHAT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
export const CHAT_DRAFT_LIMITS = Object.freeze({ text: 64000, pasteText: 64000, pastes: 20, record: 128000, total: 512000, drafts: 20 });
const PREFIX = 'henosis.chatRecovery.v1:';
const MODEL_PREFIX = 'henosis.selectedModel.scope:';
const fail = (code, message) => ({ ok: false, persisted: false, error: { code, message } });
export const draftFailure = fail;

export function normalizeChatReturnPath(path) {
  if (typeof path !== 'string' || path.length > 206 || /\s/.test(path)) return null;
  if (path === '/chat') return path;
  // No queries, fragments, encoding, slashes, dot segments or remote workspaces.
  return /^\/chat\/[A-Za-z0-9_-]{1,200}$/.test(path) ? path : null;
}

export function chatRouteForThread(threadId) {
  return typeof threadId === 'string' && !/\s/.test(threadId) && /^[A-Za-z0-9_-]{1,200}$/.test(threadId)
    ? `/chat/${threadId}` : null;
}

export function scopedSessionModelKey(authScope) {
  return typeof authScope === 'string' && authScope.trim() && authScope.length <= 200
    ? `${MODEL_PREFIX}${encodeURIComponent(authScope)}` : null;
}

function accountKey(authScope) {
  const modelKey = scopedSessionModelKey(authScope);
  return modelKey ? `${PREFIX}${encodeURIComponent(authScope)}` : null;
}
const storageFor = (storage) => storage === undefined ? window.sessionStorage : storage;
const validModel = (model) => typeof model === 'string' && !/\s/.test(model) && /^[A-Za-z0-9_.:/-]{1,128}$/.test(model);
// Never serialize credential fields, File/Blob objects, or inline binary formats.
const safeText = (text, limit) => typeof text === 'string' && text.length <= limit
  && !/data:[^\s]*;base64,|\[!audio\]|-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----|\bBearer\s+[A-Za-z0-9._-]+|\bsk-(?:proj-)?[A-Za-z0-9_-]{20,}/i.test(text);

/** Current uploaded-image records have no documented durable URL/expiry contract.
 * Omit them (including signed URLs) rather than restore a send-incomplete record.
 * Text pastes are reconstructed from ONLY the send/display fields below.
 */
export function projectDraft({ inputMessage = '', model, pastes = [], needsReattachCount = 0 } = {}) {
  if (!safeText(inputMessage, CHAT_DRAFT_LIMITS.text) || !validModel(model)) {
    return fail('DRAFT_INVALID_OR_TOO_LARGE', 'Draft text/model cannot be safely stored within the recovery limits.');
  }
  if (!Array.isArray(pastes) || pastes.length > 1000) return fail('DRAFT_TOO_LARGE', 'Too many draft attachments.');
  const safePastes = [];
  let omitted = Number.isInteger(needsReattachCount) && needsReattachCount >= 0 ? Math.min(needsReattachCount, 1000) : 0;
  for (const paste of pastes) {
    if (safePastes.length < CHAT_DRAFT_LIMITS.pastes && paste?.type === 'text'
      && safeText(paste.content, CHAT_DRAFT_LIMITS.pasteText)
      && typeof paste.fileName === 'string' && paste.fileName.length <= 255) {
      safePastes.push({ type: 'text', content: paste.content, fileName: paste.fileName });
    } else omitted += 1;
  }
  const draft = { inputMessage, model, pastes: safePastes, needsReattachCount: Math.min(omitted, 1000) };
  if (JSON.stringify(draft).length > CHAT_DRAFT_LIMITS.record) return fail('DRAFT_TOO_LARGE', 'Draft exceeds the recovery size limit; it has not been saved.');
  return { ok: true, draft, needsReattachCount: draft.needsReattachCount };
}

export function restoredModelStatus(model, availableModels = modelsData) {
  const item = availableModels.find(entry => (typeof entry === 'string' ? entry : entry.value) === model);
  const retired = item && typeof item !== 'string' && isModelPastFrontendSunset(item);
  return item && !retired ? null : {
    model, confirmationNeeded: true, reason: retired ? 'retired' : 'unavailable',
    message: `The restored model (${model}) is ${retired ? 'retired' : 'unavailable'}. Confirm a model before sending.`,
  };
}

function loadAccount(authScope, storage, now) {
  const key = accountKey(authScope);
  if (!key) return fail('INVALID_AUTH_SCOPE', 'A stable account scope is required.');
  try {
    const store = storageFor(storage);
    const raw = store.getItem(key);
    if (raw && raw.length > CHAT_DRAFT_LIMITS.total) return fail('STORAGE_BOUNDS', 'Stored recovery data exceeds its limit.');
    const data = raw ? JSON.parse(raw) : { version: 1, drafts: {}, returnTo: null };
    if (data.version !== 1 || !data.drafts || Array.isArray(data.drafts) || typeof data.drafts !== 'object'
      || Object.keys(data.drafts).length > CHAT_DRAFT_LIMITS.drafts) return fail('STORAGE_INVALID', 'Stored recovery data is invalid.');
    const drafts = {};
    for (const [route, draft] of Object.entries(data.drafts)) {
      if (!normalizeChatReturnPath(route) || !Number.isFinite(draft?.updatedAt)
        || draft.updatedAt > now || now - draft.updatedAt >= CHAT_DRAFT_TTL_MS) continue;
      const projected = projectDraft(draft);
      if (!projected.ok) return fail('STORAGE_INVALID', 'Stored draft is invalid.');
      drafts[route] = { ...projected.draft, updatedAt: draft.updatedAt };
    }
    const pointer = data.returnTo;
    const returnTo = normalizeChatReturnPath(pointer?.path) && Number.isFinite(pointer?.updatedAt)
      && pointer.updatedAt <= now && now - pointer.updatedAt < CHAT_DRAFT_TTL_MS
      ? { path: pointer.path, updatedAt: pointer.updatedAt } : null;
    return { ok: true, key, store, data: { version: 1, drafts, returnTo } };
  } catch (_) { return fail('STORAGE_UNAVAILABLE', 'Tab-local draft storage is unavailable or unreadable.'); }
}

function writeAccount(account) {
  const serialized = JSON.stringify(account.data);
  if (serialized.length > CHAT_DRAFT_LIMITS.total || Object.keys(account.data.drafts).length > CHAT_DRAFT_LIMITS.drafts) {
    return fail('STORAGE_BOUNDS', 'Recovery storage is full; this draft has not been saved.');
  }
  try {
    account.store.setItem(account.key, serialized);
    return { ok: true, persisted: true };
  } catch (_) { return fail('STORAGE_WRITE_FAILED', 'Draft could not be saved in this tab.'); }
}

export function readChatDraft(authScope, routeKey, { storage, now = Date.now() } = {}) {
  const route = normalizeChatReturnPath(routeKey);
  if (!route) return fail('INVALID_ROUTE', 'A chat pathname is required.');
  const account = loadAccount(authScope, storage, now);
  return account.ok ? { ok: true, draft: account.data.drafts[route] || null } : account;
}

export function saveChatDraft(authScope, routeKey, value, { storage, now = Date.now() } = {}) {
  const route = normalizeChatReturnPath(routeKey);
  if (!route) return fail('INVALID_ROUTE', 'A chat pathname is required.');
  const projected = projectDraft(value);
  if (!projected.ok) return projected;
  const account = loadAccount(authScope, storage, now);
  if (!account.ok) return account;
  account.data.drafts[route] = { ...projected.draft, updatedAt: now };
  return { ...writeAccount(account), needsReattachCount: projected.needsReattachCount };
}

/** Atomic single-key move: never delete the landing draft before saving target. */
export function transferChatDraft(authScope, fromRoute, threadId, value, { storage, now = Date.now() } = {}) {
  const target = chatRouteForThread(threadId);
  if (fromRoute !== CHAT_DRAFT_LANDING || !target) return fail('INVALID_TRANSFER', 'Only a landing draft can move to a new chat.');
  const projected = projectDraft(value);
  if (!projected.ok) return projected;
  const account = loadAccount(authScope, storage, now);
  if (!account.ok) return account;
  if (account.data.drafts[target]) return fail('TARGET_DRAFT_EXISTS', 'The destination already has a draft.');
  account.data.drafts[target] = { ...projected.draft, updatedAt: now };
  delete account.data.drafts[fromRoute];
  if (account.data.returnTo?.path === fromRoute) account.data.returnTo = { path: target, updatedAt: now };
  return { ...writeAccount(account), routeKey: target, needsReattachCount: projected.needsReattachCount };
}

export function rememberChatReturn(authScope, path, { storage, now = Date.now() } = {}) {
  if (!normalizeChatReturnPath(path)) return fail('INVALID_RETURN_PATH', 'Wallet return must be a chat pathname.');
  const account = loadAccount(authScope, storage, now);
  if (!account.ok) return account;
  account.data.returnTo = { path, updatedAt: now };
  return writeAccount(account);
}

/** Non-consuming read supports wallet reload and browser Back. Never navigates. */
export function readChatReturn(authScope, { storage, now = Date.now() } = {}) {
  const account = loadAccount(authScope, storage, now);
  return account.ok ? { ok: true, path: account.data.returnTo?.path || null } : account;
}

export function clearChatReturn(authScope, { storage, now = Date.now() } = {}) {
  const account = loadAccount(authScope, storage, now);
  if (!account.ok) return account;
  account.data.returnTo = null;
  return writeAccount(account);
}

/** Call only on explicit logout, before unmounting/fencing the recovery hook. */
export function clearChatRecovery(authScope, { storage } = {}) {
  const key = accountKey(authScope);
  if (!key) return fail('INVALID_AUTH_SCOPE', 'A stable account scope is required.');
  try {
    const store = storageFor(storage);
    store.removeItem(key);
    store.removeItem(scopedSessionModelKey(authScope));
    return { ok: true, persisted: true };
  } catch (_) { return fail('STORAGE_CLEAR_FAILED', 'Could not clear tab-local recovery data.'); }
}
